// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { Mutex } from 'async-mutex';
import { MP4Reader, Bytestream } from './3rdparty/mp4';
import ZipDecoder from './unzip_imgs.worker';
import H264Decoder from './3rdparty/Decoder.worker';

export enum BlockType {
    MP4VIDEO = 'mp4video',
    ARCHIVE = 'archive',
}

export enum DimensionType {
    DIMENSION_3D = '3d',
    DIMENSION_2D = '2d',
}

export function decodeZip(
    block: any, start: number, end: number, dimension: any,
): Promise<Record<string, ImageBitmap>> {
    return new Promise((resolve, reject) => {
        decodeZip.mutex.acquire().then((release) => {
            const worker = new (ZipDecoder as any)() as DecodeWorker;
            const result: Record<string, ImageBitmap> = {};
            let decoded = 0;

            worker.onerror = (e: ErrorEvent) => {
                release();
                worker.terminate();
                reject(new Error(`Archive can not be decoded. ${e.message}`));
            };

            worker.onmessage = async (event) => {
                const { error, fileName } = event.data;
                if (error) {
                    worker.onerror(new ErrorEvent('error', { message: error.toString() }));
                }

                const { data } = event.data;
                result[fileName.split('.')[0]] = data;
                decoded++;

                if (decoded === end) {
                    release();
                    worker.terminate();
                    resolve(result);
                }
            };

            worker.postMessage({
                block,
                start,
                end,
                dimension,
                dimension2D: DimensionType.DIMENSION_2D,
            });
        });
    });
}

decodeZip.mutex = new Mutex();

interface BlockToDecode {
    start: number;
    end: number;
    block: ArrayBuffer;
    resolveCallback: ((frame: number) => void) | null;
    rejectCallback: ((e?: ErrorEvent) => void) | null;
}

interface VideoDecodeTask {
    rangeKey: string;
    start: number;
    end: number;
    index: number;
}

interface VideoDecodeMessage {
    buf: Uint8Array;
    offset: number;
    length: number;
}

interface VideoDecodeWorkerState {
    worker: DecodeWorker;
    ready: boolean;
    task: VideoDecodeTask | null;
    bufferedMessages: VideoDecodeMessage[];
}

interface DecodeWorker {
    onmessage: ((event: MessageEvent<any>) => void) | null;
    onerror: ((event: ErrorEvent) => void) | null;
    postMessage(message: any): void;
    terminate(): void;
}

export class FrameProvider {
    private blocksRanges: string[];
    private blockSize: number;
    private blockType: BlockType;
    private _blocks: Record<number, ArrayBuffer | 'loading'>;

    /*
        ImageBitmap when decode zip chunks
        ImageData when decode video chunks
        Blob when 3D dimension
        null when not decoded yet
    */
    private frames: Record<string, ImageBitmap | ImageData | Blob | null>;
    private requestedBlockToDecode: null | BlockToDecode;
    private blocksAreBeingDecoded: Record<string, BlockToDecode>;
    private promisedFrames: Record<string, {
        resolve: (data: ImageBitmap | ImageData | Blob) => void;
        reject: () => void;
    }>;
    private currentDecodingThreads: number;
    private currentFrame: number;
    private mutex: Mutex;

    private dimension: DimensionType;
    private workerThreadsLimit: number;
    private cachedEncodedBlocksLimit: number;
    private cachedDecodedBlocksLimit: number;
    private decodeStartScheduled: boolean;
    private videoWorkers: VideoDecodeWorkerState[];

    // used for video chunks to resize after decoding
    private renderWidth: number;
    private renderHeight: number;

    constructor(
        blockType: BlockType,
        blockSize: number,
        cachedBlockCount: number,
        decodedBlocksCacheSize = 5,
        maxWorkerThreadCount = 2,
        dimension: DimensionType = DimensionType.DIMENSION_2D,
    ) {
        this.mutex = new Mutex();
        this.blocksRanges = [];
        this.frames = {};
        this.promisedFrames = {};
        this.currentDecodingThreads = 0;
        this.currentFrame = -1;

        this.cachedEncodedBlocksLimit = Math.max(1, cachedBlockCount); // number of stored blocks
        this.cachedDecodedBlocksLimit = decodedBlocksCacheSize;
        this.workerThreadsLimit = maxWorkerThreadCount;
        this.dimension = dimension;
        this.decodeStartScheduled = false;
        this.videoWorkers = [];

        this.renderWidth = 1920;
        this.renderHeight = 1080;
        this.blockSize = blockSize;
        this.blockType = blockType;

        // todo: sort out with logic of blocks
        this._blocks = {};
        this.requestedBlockToDecode = null;
        this.blocksAreBeingDecoded = {};
    }

    isChunkCached(start: number, end: number): boolean {
        return this.blocksRanges.includes(`${start}:${end}`);
    }

    private _scheduleDecode(): void {
        if (this.decodeStartScheduled) {
            return;
        }

        this.decodeStartScheduled = true;
        Promise.resolve().then(async () => {
            this.decodeStartScheduled = false;
            while (this.requestedBlockToDecode !== null && this.currentDecodingThreads < this.workerThreadsLimit) {
                await this.startDecode();
            }
        }).catch((error: Error) => {
            this.decodeStartScheduled = false;
            throw error;
        });
    }

    private _flushVideoWorker(workerState: VideoDecodeWorkerState): void {
        if (!workerState.ready || !workerState.bufferedMessages.length) {
            return;
        }

        for (const message of workerState.bufferedMessages) {
            workerState.worker.postMessage(message);
        }
        workerState.bufferedMessages = [];
    }

    private _releaseVideoWorker(workerState: VideoDecodeWorkerState, terminate = false): void {
        workerState.task = null;
        workerState.bufferedMessages = [];
        this.currentDecodingThreads = Math.max(0, this.currentDecodingThreads - 1);

        if (terminate) {
            workerState.worker.terminate();
            this.videoWorkers = this.videoWorkers.filter((state) => state !== workerState);
        }

        this._scheduleDecode();
    }

    private _createVideoWorker(): VideoDecodeWorkerState {
        const worker = new (H264Decoder as any)() as DecodeWorker;
        const workerState: VideoDecodeWorkerState = {
            worker,
            ready: false,
            task: null,
            bufferedMessages: [],
        };

        worker.onmessage = (e) => {
            if (e.data.consoleLog) {
                workerState.ready = true;
                this._flushVideoWorker(workerState);
                return;
            }

            const task = workerState.task;
            if (!task) {
                return;
            }

            const activeBlock = this.blocksAreBeingDecoded[task.rangeKey];
            if (!activeBlock) {
                if (task.index === task.end) {
                    this._releaseVideoWorker(workerState);
                } else {
                    task.index++;
                }
                return;
            }

            const scaleFactor = Math.ceil(this.renderHeight / e.data.height);
            this.frames[task.index] = FrameProvider.cropImage(
                e.data.buf,
                e.data.width,
                e.data.height,
                0,
                0,
                Math.floor(this.renderWidth / scaleFactor),
                Math.floor(this.renderHeight / scaleFactor),
            );

            if (activeBlock.resolveCallback) {
                activeBlock.resolveCallback(task.index);
            }

            if (task.index in this.promisedFrames) {
                const { resolve } = this.promisedFrames[task.index];
                delete this.promisedFrames[task.index];
                resolve(this.frames[task.index]);
            }

            if (task.index === task.end) {
                delete this.blocksAreBeingDecoded[task.rangeKey];
                this._releaseVideoWorker(workerState);
                return;
            }

            task.index++;
        };

        worker.onerror = (e: ErrorEvent) => {
            const task = workerState.task;
            if (task) {
                const activeBlock = this.blocksAreBeingDecoded[task.rangeKey];
                for (let i = task.index; i <= task.end; i++) {
                    if (i in this.promisedFrames) {
                        const { reject } = this.promisedFrames[i];
                        delete this.promisedFrames[i];
                        reject();
                    }
                }

                if (activeBlock?.rejectCallback) {
                    activeBlock.rejectCallback(e);
                }

                delete this.blocksAreBeingDecoded[task.rangeKey];
            }

            this._releaseVideoWorker(workerState, true);
        };

        worker.postMessage({
            type: 'Broadway.js - Worker init',
            options: {
                rgb: true,
                reuseMemory: false,
            },
        });

        this.videoWorkers.push(workerState);
        return workerState;
    }

    private _acquireVideoWorker(): VideoDecodeWorkerState {
        const idleWorker = this.videoWorkers.find((state) => !state.task);
        if (idleWorker) {
            return idleWorker;
        }

        if (this.videoWorkers.length < this.workerThreadsLimit) {
            return this._createVideoWorker();
        }

        throw new Error('No available video decode workers');
    }

    private _buildVideoDecodeMessages(block: ArrayBuffer): VideoDecodeMessage[] {
        const reader = new MP4Reader(new Bytestream(block));
        reader.read();
        const video = reader.tracks[1];

        const avc = video.trak.mdia.minf.stbl.stsd.avc1.avcC;
        const messages: VideoDecodeMessage[] = [
            { buf: avc.sps[0], offset: 0, length: avc.sps[0].length },
            { buf: avc.pps[0], offset: 0, length: avc.pps[0].length },
        ];

        for (let sample = 0; sample < video.getSampleCount(); sample++) {
            for (const nal of video.getSampleNALUnits(sample)) {
                messages.push({
                    buf: nal,
                    offset: 0,
                    length: nal.length,
                });
            }
        }

        return messages;
    }

    /* This method removes extra data from a cache when memory overflow */
    async _cleanup(): Promise<void> {
        if (this.blocksRanges.length > this.cachedEncodedBlocksLimit) {
            const shifted = this.blocksRanges.shift(); // get the oldest block
            const [start, end] = shifted.split(':').map((el) => +el);
            delete this._blocks[Math.floor(start / this.blockSize)];
            for (let i = start; i <= end; i++) {
                delete this.frames[i];
            }
        }

        // delete frames whose are not in areas of current frame
        const distance = Math.floor(this.cachedDecodedBlocksLimit / 2);
        for (let i = 0; i < this.blocksRanges.length; i++) {
            const [start, end] = this.blocksRanges[i].split(':').map((el) => +el);
            if (
                end < this.currentFrame - distance * this.blockSize ||
                start > this.currentFrame + distance * this.blockSize
            ) {
                for (let j = start; j <= end; j++) {
                    delete this.frames[j];
                }
            }
        }
    }

    async requestDecodeBlock(
        block: ArrayBuffer,
        start: number,
        end: number,
        resolveCallback: ((frame: number) => void) | null,
        rejectCallback: ((e?: ErrorEvent) => void) | null,
    ): Promise<void> {
        const release = await this.mutex.acquire();
        try {
            if (this.requestedBlockToDecode !== null) {
                if (start === this.requestedBlockToDecode.start && end === this.requestedBlockToDecode.end) {
                    // only rewrite callbacks if the same block was requested again
                    this.requestedBlockToDecode.resolveCallback = resolveCallback;
                    this.requestedBlockToDecode.rejectCallback = rejectCallback;

                    // todo: should we reject the previous request here?
                } else if (this.requestedBlockToDecode.rejectCallback) {
                    // if another block requested, the previous request should be rejected
                    this.requestedBlockToDecode.rejectCallback();
                }
            }

            if (!(`${start}:${end}` in this.blocksAreBeingDecoded)) {
                this.requestedBlockToDecode = {
                    block: block || this._blocks[Math.floor(start / this.blockSize)],
                    start,
                    end,
                    resolveCallback,
                    rejectCallback,
                };
            } else {
                this.blocksAreBeingDecoded[`${start}:${end}`].rejectCallback = rejectCallback;
                this.blocksAreBeingDecoded[`${start}:${end}`].resolveCallback = resolveCallback;
            }
        } finally {
            release();
        }

        this._scheduleDecode();
    }

    setRenderSize(width: number, height: number): void {
        this.renderWidth = width;
        this.renderHeight = height;
    }

    /* Method returns frame from collection. Else method returns null */
    async frame(frameNumber: number): Promise<ImageBitmap | ImageData | Blob> {
        this.currentFrame = frameNumber;
        return new Promise((resolve, reject) => {
            if (frameNumber in this.frames) {
                if (this.frames[frameNumber] !== null) {
                    resolve(this.frames[frameNumber]);
                } else {
                    this.promisedFrames[frameNumber] = { resolve, reject };
                }
            } else {
                resolve(null);
            }
        });
    }

    isNextChunkExists(frameNumber: number): boolean {
        const nextChunkNum = Math.floor(frameNumber / this.blockSize) + 1;
        return nextChunkNum in this._blocks;
    }

    setReadyToLoading(chunkNumber: number): void {
        this._blocks[chunkNumber] = 'loading';
    }

    static cropImage(
        imageBuffer: ArrayBuffer,
        imageWidth: number,
        imageHeight: number,
        xOffset: number,
        yOffset: number,
        width: number,
        height: number,
    ): ImageData {
        if (xOffset === 0 && width === imageWidth && yOffset === 0 && height === imageHeight) {
            return new ImageData(new Uint8ClampedArray(imageBuffer), width, height);
        }
        const source = new Uint32Array(imageBuffer);

        const bufferSize = width * height * 4;
        const buffer = new ArrayBuffer(bufferSize);
        const rgbaInt32 = new Uint32Array(buffer);
        const rgbaInt8Clamped = new Uint8ClampedArray(buffer);

        if (imageWidth === width) {
            return new ImageData(new Uint8ClampedArray(imageBuffer, yOffset * 4, bufferSize), width, height);
        }

        let writeIdx = 0;
        for (let row = yOffset; row < height; row++) {
            const start = row * imageWidth + xOffset;
            rgbaInt32.set(source.subarray(start, start + width), writeIdx);
            writeIdx += width;
        }

        return new ImageData(rgbaInt8Clamped, width, height);
    }

    async startDecode(): Promise<void> {
        const release = await this.mutex.acquire();
        try {
            const requestedBlock = this.requestedBlockToDecode;
            if (!requestedBlock) {
                return;
            }

            const { start, end, block } = requestedBlock;

            this.blocksRanges.push(`${start}:${end}`);
            this.blocksAreBeingDecoded[`${start}:${end}`] = requestedBlock;
            this.requestedBlockToDecode = null;
            this._blocks[Math.floor((start + 1) / this.blockSize)] = block;

            for (let i = start; i <= end; i++) {
                this.frames[i] = null;
            }

            this._cleanup();
            this.currentDecodingThreads++;

            if (this.blockType === BlockType.MP4VIDEO) {
                const workerState = this._acquireVideoWorker();
                workerState.task = {
                    rangeKey: `${start}:${end}`,
                    start,
                    end,
                    index: start,
                };
                workerState.bufferedMessages = this._buildVideoDecodeMessages(block);
                this._flushVideoWorker(workerState);
            } else {
                const worker = new ZipDecoder();
                let index = start;

                worker.onmessage = async (event) => {
                    this.frames[event.data.index] = event.data.data;

                    const { resolveCallback } = this.blocksAreBeingDecoded[`${start}:${end}`];
                    if (resolveCallback) {
                        resolveCallback(event.data.index);
                    }

                    if (event.data.index in this.promisedFrames) {
                        const { resolve } = this.promisedFrames[event.data.index];
                        delete this.promisedFrames[event.data.index];
                        resolve(this.frames[event.data.index]);
                    }

                    if (index === end) {
                        worker.terminate();
                        this.currentDecodingThreads--;
                        delete this.blocksAreBeingDecoded[`${start}:${end}`];
                        this._scheduleDecode();
                    }
                    index++;
                };

                worker.onerror = (e: ErrorEvent) => {
                    for (let i = start; i <= end; i++) {
                        if (i in this.promisedFrames) {
                            const { reject } = this.promisedFrames[i];
                            delete this.promisedFrames[i];
                            reject();
                        }
                    }
                    const activeBlock = this.blocksAreBeingDecoded[`${start}:${end}`];
                    if (activeBlock?.rejectCallback) {
                        activeBlock.rejectCallback(e);
                    }
                    delete this.blocksAreBeingDecoded[`${start}:${end}`];
                    this.currentDecodingThreads--;
                    worker.terminate();
                    this._scheduleDecode();
                };

                worker.postMessage({
                    block,
                    start,
                    end,
                    dimension: this.dimension,
                    dimension2D: DimensionType.DIMENSION_2D,
                });
            }
        } finally {
            release();
        }
    }

    get decodedBlocksCacheSize(): number {
        return this.cachedDecodedBlocksLimit;
    }

    /*
        Method returns a list of cached ranges
        Is an array of strings like "start:end"
    */
    get cachedFrames(): string[] {
        return [...this.blocksRanges].sort((a, b) => +a.split(':')[0] - +b.split(':')[0]);
    }
}
